/**
 * Wave DOC (decision 0068, spec `docs/design/documents-2026-10-12/spec.md` §3, §5): documents in a project. A document
 * is an artifact with `doc` set (reference material a person added, project-level). Pure; shared by decide, the cloud
 * (MCP tools, the view route) and the web (the documents section and page).
 *
 * Version chains, heads and refs are the artifacts' own (`outputs.ts`: `versionsOf`, `isLive`, `artifactByRef`).
 */
import {
  claudeArtifactOf,
  type DocKind,
  docFolderOf,
  docHomeOf,
  docKindOf,
  docPathKey,
  docUrlHost,
  isFileUri,
  isUnderDocFolder,
  type LoadedDocumentActor,
  pageKindOf,
  VIEW_MAX_FILES,
} from "@minga/protocol"
import { forbidden, invalid, reject, type Tx } from "./decide/tx"
import { isLive, versionsOf } from "./outputs"
// a cycle (pages reads documents' lookups); both only call each other at decide time, never at module load
import { lockedPageRefusal } from "./pages"
import { agentOf, artifactOf } from "./state"
import type { Actor, Artifact, Command, ProjectState } from "./types"

type PostArtifactCmd = Extract<Command, { _tag: "PostArtifact" }>

type Artifacts = Pick<ProjectState, "artifacts">

/** Has `doc` set: a document (or a version of one), not an output. */
export const isDocument = (a: Artifact): boolean => a.doc !== undefined

/** A document's path, or null (a link added without a path). */
export const docPathOf = (a: Artifact): string | null => a.doc?.path ?? null

const orderKey = (a: Artifact): string => {
  const p = docPathOf(a)
  // path documents by path; path-less links after them, by title
  return p === null ? `￿${a.title.toLowerCase()}` : docPathKey(p)
}

/**
 * The live documents (not rejected, not superseded by a non-rejected version), ordered by path, path-less links last.
 * `under`: a path prefix ("docs/design/" or "docs/design"; case-insensitive; path-less links are left out). `kinds`:
 * only these kinds ({@link docKindOf}).
 */
export const documentHeads = (
  s: Artifacts,
  opts: { readonly under?: string; readonly kinds?: ReadonlyArray<DocKind> } = {},
): ReadonlyArray<Artifact> => {
  const under = opts.under === undefined ? undefined : docPathKey(opts.under)
  const out = s.artifacts.filter((a) => {
    if (!isDocument(a) || !isLive(s, a)) return false
    if (under !== undefined && under !== "") {
      const p = docPathOf(a)
      if (p === null || !docPathKey(p).startsWith(under)) return false
    }
    return opts.kinds === undefined || opts.kinds.includes(docKindOf(a))
  })
  return [...out].sort((x, y) => (orderKey(x) < orderKey(y) ? -1 : orderKey(x) > orderKey(y) ? 1 : 0))
}

export const notebookOf = (s: Artifacts, agentId: string): Artifact | undefined =>
  documentHeads(s).find((a) => a.doc?.notebookFor === agentId)

/** The live document at `path` (case-insensitive, {@link docPathKey}). */
export const documentByPath = (s: Artifacts, path: string): Artifact | undefined => {
  const key = docPathKey(path)
  return s.artifacts.find((a) => {
    const p = docPathOf(a)
    return p !== null && docPathKey(p) === key && isLive(s, a)
  })
}

/** The live path-less link document with this url. */
export const documentByUrl = (s: Artifacts, url: string): Artifact | undefined =>
  s.artifacts.find((a) => a.doc !== undefined && a.doc.path === null && a.doc.url === url && isLive(s, a))

/** What a person is about to post as the next version of a document (the fields `unchangedDocument` compares). */
export interface DocDraft {
  readonly uri: string
  readonly sha256: string | null
  readonly title: string
  /** The notes (stored in `preview`). */
  readonly preview: string | null
}

/** Same bytes, or (links) the same url, title and notes: nothing to post. A file never equals a link. */
export const unchangedDocument = (head: Artifact, next: DocDraft): boolean => {
  if (head.sha256 !== null || next.sha256 !== null) return head.sha256 === next.sha256
  return (
    head.uri === next.uri && head.title === next.title && (head.preview ?? null) === (next.preview ?? null)
  )
}

/** "Jannik", "Lea (agent)": the name a document byline shows ("Minga" for system processes and unknown actors). */
export const docActorName = (
  s: Pick<ProjectState, "members" | "agents">,
  a: Actor | LoadedDocumentActor,
): string => {
  switch (a._tag) {
    case "user":
      return s.members.find((m) => m.userId === a.userId)?.displayName ?? "someone"
    case "agent":
    case "worker":
      return `${s.agents.find((x) => x.id === a.agentId)?.name ?? "an agent"} (agent)`
    default:
      return "Minga"
  }
}

/** Why scripts are off in a view; null when they run. */
export type ScriptsOff =
  | { readonly _tag: "author"; readonly actor: Actor }
  | { readonly _tag: "tainted" }
  /** The version is a person's, but its bytes were first posted by an agent (a Restore of an agent's version). */
  | { readonly _tag: "bytes_author"; readonly actor: Actor }
  | { readonly _tag: "support_author"; readonly path: string; readonly actor: Actor }
  | { readonly _tag: "support_tainted"; readonly path: string }
  | { readonly _tag: "not_html" }

/** The grey line under a scripts-off frame ("Scripts are off: Lea (agent) wrote this version"). */
export const scriptsOffLine = (off: ScriptsOff, name: (a: Actor) => string): string => {
  switch (off._tag) {
    case "author":
      return `Scripts are off: ${name(off.actor)} wrote this version`
    case "tainted":
      return "Scripts are off: from outside the team"
    case "bytes_author":
      return `Scripts are off: ${name(off.actor)} wrote these bytes`
    case "support_author":
      return `Scripts are off: ${name(off.actor)} wrote ${off.path}`
    case "support_tainted":
      return `Scripts are off: ${off.path} is from outside the team`
    case "not_html":
      return "Scripts are off: not an HTML page"
  }
}

export interface DocView {
  /** The entry's path (the version's own). */
  readonly entry: string
  readonly scripts: boolean
  readonly off: ScriptsOff | null
  /** {@link scriptsOffLine} with {@link docActorName}; null when scripts run. */
  readonly reason: string | null
  /** The bytes the view may read: the entry first, then support files and images under its folder, by path. */
  readonly files: ReadonlyArray<{ readonly path: string; readonly sha256: string }>
  /** Other documents under the folder: served as "open in Minga" stubs, never as bytes. */
  readonly pages: ReadonlyArray<{ readonly path: string; readonly ref: string }>
  /** More than {@link VIEW_MAX_FILES} files (or pages) were under the folder; the rest were left out. */
  readonly truncated: boolean
}

const isPerson = (a: Actor): boolean => a._tag === "user"

/**
 * Where bytes came from, not only who posted this version (decision 0068, spec §15 decision 3): scripts run only in
 * bytes a person posted untainted. Any artifact in the project with the same sha256 that a non-person created, or that
 * is tainted, makes the bytes untrusted, so restoring (or re-posting) an agent's or an outside version keeps its scripts
 * off. Null when every artifact with these bytes is a person's and untainted.
 */
export const bytesDistrust = (
  s: Artifacts,
  sha256: string,
): { readonly _tag: "author"; readonly actor: Actor } | { readonly _tag: "tainted" } | null => {
  let tainted = false
  for (const x of s.artifacts) {
    if (x.sha256 !== sha256) continue
    if (!isPerson(x.createdBy)) return { _tag: "author", actor: x.createdBy }
    if (x.tainted) tainted = true
  }
  return tainted ? { _tag: "tainted" } : null
}

/** True when only people posted these bytes, untainted ({@link bytesDistrust}). */
export const bytesTrusted = (s: Artifacts, sha256: string): boolean => bytesDistrust(s, sha256) === null

/**
 * What an HTML view of document version `a` may read and whether its scripts run (spec §5.3). The server builds it,
 * never the caller. Reads: the entry's own bytes; live support files (JS, CSS, fonts) and images in the entry's folder
 * and below; nothing tainted when the entry is untainted. Other live documents under the folder are `pages`. Scripts
 * run only when the entry is HTML, its version was created by a person, it is untainted, and every support file in
 * the view was created by a person and is untainted, and no agent or outside artifact in the project has the same
 * bytes as the entry or a support file ({@link bytesDistrust}: a person's Restore of an agent's version stays off).
 *
 * Null when `a` is not a file document with a path.
 */
export const viewOf = (s: ProjectState, a: Artifact): DocView | null => {
  const entry = docPathOf(a)
  if (entry === null || a.sha256 === null) return null
  const folder = docFolderOf(entry)
  const entryKey = docPathKey(entry)
  const files: Array<{ path: string; sha256: string }> = [{ path: entry, sha256: a.sha256 }]
  const pages: Array<{ path: string; ref: string }> = []
  let truncated = false
  let off: ScriptsOff | null = null
  if (docKindOf(a) !== "html") off = { _tag: "not_html" }
  else if (!isPerson(a.createdBy)) off = { _tag: "author", actor: a.createdBy }
  else if (a.tainted) off = { _tag: "tainted" }
  else {
    const d = bytesDistrust(s, a.sha256)
    if (d !== null) off = d._tag === "author" ? { _tag: "bytes_author", actor: d.actor } : { _tag: "tainted" }
  }
  for (const d of documentHeads(s)) {
    const path = docPathOf(d)
    if (path === null || docPathKey(path) === entryKey || !isUnderDocFolder(path, folder)) continue
    const kind = docKindOf(d)
    if (kind === "support" || kind === "image") {
      if (d.sha256 === null) continue
      if (d.tainted && !a.tainted) continue // no tainted bytes in an untainted view
      if (files.length >= VIEW_MAX_FILES) {
        truncated = true
        continue
      }
      files.push({ path, sha256: d.sha256 })
      if (kind === "support" && off === null) {
        const bytes = d.tainted ? ({ _tag: "tainted" } as const) : bytesDistrust(s, d.sha256)
        if (!isPerson(d.createdBy)) off = { _tag: "support_author", path, actor: d.createdBy }
        else if (bytes?._tag === "author") off = { _tag: "support_author", path, actor: bytes.actor }
        else if (bytes !== null) off = { _tag: "support_tainted", path }
      }
    } else if (kind !== "link" && d.ref !== undefined) {
      if (pages.length >= VIEW_MAX_FILES) {
        truncated = true
        continue
      }
      pages.push({ path, ref: d.ref })
    }
  }
  return {
    entry,
    scripts: off === null,
    off,
    reason: off === null ? null : scriptsOffLine(off, (x) => docActorName(s, x)),
    files,
    pages,
    truncated,
  }
}

/**
 * "v3 · Updated by Lea (agent)", "v1 · Added by Jannik", "v3 · Restored v1 by Jannik" (the version's bytes, or a
 * link's url, title and notes, equal an older version's). The web appends the time ("· 2 h ago").
 */
export const docByline = (s: ProjectState, a: Artifact, names: (actor: Actor) => string): string => {
  const chain = versionsOf(s, a)
  const n = chain.length
  const who = names(a.createdBy)
  if (n === 1) return `v1 · Added by ${who}`
  const older = chain.slice(0, -2) // the version right before is never "restored" (it would be unchanged)
  for (let i = older.length - 1; i >= 0; i--) {
    const o = older[i]
    if (o && unchangedDocument(o, a)) return `v${n} · Restored v${i + 1} by ${who}`
  }
  return `v${n} · Updated by ${who}`
}

/** The words search finds a document by besides its title and notes: its path (`/`, `-`, `_` as spaces), and for a
 * url "Claude artifact" (a Claude link) and the url's host. Empty for an output. */
export const docSearchText = (a: Artifact): string => {
  if (a.doc === undefined) return ""
  const parts: string[] = []
  if (a.doc.path !== null) parts.push(a.doc.path.replace(/[/_-]+/g, " "))
  if (a.doc.url !== null) {
    if (claudeArtifactOf(a.doc.url)) parts.push("Claude artifact")
    const host = docUrlHost(a.doc.url)
    if (host !== null) parts.push(host)
  }
  return parts.join("\n")
}

const fileNameOf = (path: string): string => path.slice(path.lastIndexOf("/") + 1)

const TITLE_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
  apos: "'",
}
const decodeTitle = (s: string): string =>
  s.replace(/&(amp|lt|gt|quot|#39|apos);/g, (_m, e: string) => TITLE_ENTITIES[e] ?? "")

/**
 * A document's title: the first `# ` heading of a Markdown file (after any front matter), the `<title>` of an HTML
 * file, else the file name. At most 200 characters.
 */
export const docTitleOf = (path: string, text: string | null): string => {
  const name = fileNameOf(path)
  let title: string | null = null
  if (text !== null && /\.(md|markdown)$/i.test(name)) {
    const body = text.startsWith("---") ? text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, "") : text
    let fence = false
    for (const line of body.split(/\r?\n/)) {
      if (/^\s*(```|~~~)/.test(line)) fence = !fence
      if (fence) continue
      const m = /^#\s+(.+?)\s*#*\s*$/.exec(line)
      if (m?.[1]) {
        title = m[1]
        break
      }
    }
  } else if (text !== null && /\.html?$/i.test(name)) {
    const head = text.slice(0, 64 * 1024)
    const m = /<title[^>]*>([^<]{1,400})<\/title>/i.exec(head)
    if (m?.[1]) title = decodeTitle(m[1]).replace(/\s+/g, " ").trim()
  }
  const out = title && title.trim() !== "" ? title.trim() : name
  return out.length > 200 ? `${out.slice(0, 199)}…` : out
}

// #region decide
/**
 * Decision 0068: a new version whose bytes equal a tainted version of the same chain stays tainted (a Restore of an
 * outside version is still from outside the team). Taint only rises.
 */
export const carriesChainTaint = (
  s: Artifacts,
  c: Pick<PostArtifactCmd, "supersedes" | "sha256">,
): boolean => {
  if (c.supersedes === null || c.sha256 === null) return false
  const old = s.artifacts.find((x) => x.id === c.supersedes)
  return old !== undefined && versionsOf(s, old).some((v) => v.tainted && v.sha256 === c.sha256)
}

/**
 * Wave DOC (decision 0068, spec §3): the `doc` a posted artifact carries, after the document rules.
 * People and persistent agents create documents. Documents are project-level. A path is unique among live documents
 * (case-insensitive). 5. A path-less link is unique by url. 6. A version of a document is a document (an agent's
 * version without `doc` carries the old `doc`); a document never supersedes an output. External-home versions
 * record a new source revision instead of silently turning an imported snapshot into a second authority.
 */
export const documentOfPost = (tx: Tx, c: PostArtifactCmd): Artifact["doc"] => {
  const old = c.supersedes ? artifactOf(tx.state, c.supersedes) : undefined
  const pagesJob = tx.actor._tag === "system" && tx.actor.process === "pages"
  if (tx.actor._tag !== "user" && old?.doc && !isLive(tx.state, old))
    invalid(
      old.state === "rejected"
        ? "this document was removed; a person's removal stands"
        : "supersede the current live version",
      "supersedes",
    )
  if (tx.actor._tag !== "user" && old?.doc && docHomeOf(old.doc) === "external") {
    if (
      c.doc?.sourceRevision === undefined ||
      c.doc.sourceRevision === old.doc.sourceRevision ||
      c.doc.url !== old.doc.url ||
      docHomeOf(c.doc) !== "external"
    )
      forbidden(
        "actor_kind",
        `${old.ref ?? "this document"} lives externally: edit it in the repo or original, then import its sourceRevision`,
      )
  }
  if (c.doc === undefined) {
    if (old?.doc === undefined) {
      if (pagesJob) forbidden("actor_kind", PAGES_JOB_ONLY)
      return undefined
    }
    // a version without `doc` goes only on the live head: a removed document stays removed (a person adds it back)
    if (!isLive(tx.state, old))
      invalid(
        old.state === "rejected"
          ? `${old.ref ?? old.id} was removed from the project's documents: a person adds it back`
          : `${old.ref ?? old.id} is not the newest version: supersede the newest one`,
        "supersedes",
      )
    if (pagesJob && pageKindOf(old.doc.path ?? "") === null) forbidden("actor_kind", PAGES_JOB_ONLY)
    refusePageLock(tx, old.doc, old)
    refuseTakenPlace(tx, old.doc, c.supersedes)
    return old.doc
  }
  if (tx.actor._tag !== "user" && tx.actor._tag !== "agent" && !pagesJob)
    forbidden("actor_kind", "people and persistent project agents write project documents")
  // decision 0084 (§1.3): the pages job writes pages only (create, its own chain, a notebook move)
  if (pagesJob && pageKindOf(c.doc.path ?? "") === null) forbidden("actor_kind", PAGES_JOB_ONLY)
  if (c.itemId !== null) invalid("a document sits on the project, not on an item", "itemId")
  if (old && !isDocument(old)) invalid(`${old.ref ?? old.id} is an output, not a document`, "supersedes")
  if (!isFileUri(c.uri)) {
    if (c.doc.url === null || c.uri !== c.doc.url) invalid("a link document's uri is its doc.url", "uri")
    if (c.kind !== "link") invalid("a link document has kind link", "kind")
  }
  const doc = {
    ...c.doc,
    ...(c.doc.notebookFor === undefined && old?.doc?.notebookFor !== undefined
      ? { notebookFor: old.doc.notebookFor }
      : {}),
  }
  if (doc.notebookFor !== undefined) {
    if (!agentOf(tx.state, doc.notebookFor))
      invalid("the notebook's agent is not in this project", "doc.notebookFor")
    if (doc.path === null || !/\.md$/i.test(doc.path) || !isFileUri(c.uri) || docHomeOf(doc) !== "minga")
      invalid("an agent notebook is a Minga-home Markdown document", "doc")
    const notebook = notebookOf(tx.state, doc.notebookFor)
    if (
      notebook &&
      notebook.id !== c.supersedes &&
      !versionsOf(tx.state, notebook).some((v) => v.id === c.supersedes)
    )
      reject({
        _tag: "Duplicate",
        existing: { _tag: "artifact", id: notebook.id },
        reason: "this agent already has a notebook; post a new version of it",
      })
  }
  refusePageLock(tx, doc, old)
  refuseTakenPlace(tx, doc, c.supersedes)
  return doc
}

const PAGES_JOB_ONLY = "the pages job writes project and agent pages only"

/**
 * Decision 0084 (§2.1): the page locks, on the new path and the superseded version's (core `lockedPageRefusal`), for
 * both branches (an explicit `doc` and a version that inherits the old one). Before the path-uniqueness check, so an
 * agent writing a locked page hears about propose_page rather than about the page's existence.
 */
const refusePageLock = (tx: Tx, doc: NonNullable<Artifact["doc"]>, old: Artifact | undefined): void => {
  const why = lockedPageRefusal(tx.state, tx.actor, doc, old)
  if (why !== null) forbidden("actor_kind", why)
}

/** Rules 4 and 5: another live chain already holds this path (or, for a path-less link, this url) → Duplicate. */
const refuseTakenPlace = (
  tx: Tx,
  doc: NonNullable<Artifact["doc"]>,
  supersedes: PostArtifactCmd["supersedes"],
): void => {
  const same =
    doc.path !== null
      ? documentByPath(tx.state, doc.path)
      : doc.url !== null
        ? documentByUrl(tx.state, doc.url)
        : undefined
  // superseding an older version of the same chain falls to postArtifact's "already superseded" check
  if (same && same.id !== supersedes && !versionsOf(tx.state, same).some((v) => v.id === supersedes))
    reject({
      _tag: "Duplicate",
      existing: { _tag: "artifact", id: same.id },
      reason:
        doc.path !== null
          ? `${same.ref ?? same.id} is already at ${same.doc?.path ?? doc.path}: post a new version of it`
          : `${same.ref ?? same.id} already links ${doc.url}`,
    })
}
// #endregion decide
